import { Alert, Box, Button, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';

import {
  GUEST_FIELDS,
  type ColumnMapping,
  type FieldSpec,
  type GuestField,
  type MappingWarning,
} from '../../../lib/columnMapping';
import { TRACKER_SURFACE_SX, GUEST_PRIMARY_BUTTON_SX, GUEST_SECONDARY_BUTTON_SX } from './guestTableStyles';

const UNMAPPED = '';

export interface ImportColumnMapperProps {
  fileName: string;
  headers: string[];
  /** First few data rows, shown so the host can see what each column holds. */
  sampleRows: string[][];
  mapping: ColumnMapping;
  errors: string[];
  warnings: MappingWarning[];
  rowCount: number;
  skippedCount: number;
  busy: boolean;
  /**
   * Which fields this API can store — `guestFieldsFor` decides. Defaulted so
   * every existing caller keeps the full list, but a caller talking to an API
   * without guest tags passes the narrowed one, and the Tags dropdown is then
   * absent rather than present and inert.
   */
  fields?: readonly FieldSpec[];
  onChange: (field: GuestField, columnIndex: number | undefined) => void;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * What Tripsby thinks each column is, before anything is read from it.
 *
 * Detection is a guess from the header text, and it will sometimes be wrong on a
 * file nobody has seen before. That is survivable only because this screen
 * exists: the host reads the guess against a real sample of their own data and
 * changes any of it. Without the sample the dropdowns are an abstract quiz —
 * "is column 4 the phone?" is answerable by looking at column 4, not by its name.
 *
 * Nothing here writes. Confirming hands the mapped rows to the import plan,
 * which still shows what will be created and updated before that writes either.
 */
export default function ImportColumnMapper({
  fileName,
  headers,
  sampleRows,
  mapping,
  errors,
  warnings,
  rowCount,
  skippedCount,
  busy,
  fields = GUEST_FIELDS,
  onChange,
  onCancel,
  onConfirm,
}: ImportColumnMapperProps) {
  // One column may only feed one field: two fields reading the same column
  // silently duplicates a value into both, and there is no reading of a guest
  // list where that is what someone meant.
  const claimedBy = new Map<number, GuestField>();
  for (const [field, index] of Object.entries(mapping)) {
    if (typeof index === 'number') claimedBy.set(index, field as GuestField);
  }

  return (
    <Paper elevation={0} sx={{ ...TRACKER_SURFACE_SX, p: { xs: 2, sm: 2.5 }, mt: 2 }}>
      <Typography sx={{ color: 'var(--tsb-fg)', fontSize: 15, fontWeight: 500 }}>
        What’s in {fileName}?
      </Typography>
      <Typography sx={{ color: 'var(--tsb-fg-muted)', fontSize: 12.5, mt: 0.5, lineHeight: 1.55 }}>
        {rowCount} {rowCount === 1 ? 'row' : 'rows'} read
        {skippedCount > 0 ? `, ${skippedCount} skipped for having no name` : ''}. Check each column
        below — change anything that’s wrong before importing.
      </Typography>

      {errors.map((error) => (
        <Alert key={error} severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      ))}
      {warnings.map((warning) => (
        <Alert key={warning.field} severity="warning" sx={{ mt: 2 }}>
          {warning.message}
        </Alert>
      ))}

      <Box sx={{ overflowX: 'auto', mt: 2 }}>
        <Stack direction="row" spacing={1.5} sx={{ minWidth: 'min-content', pb: 1 }}>
          {headers.map((header, index) => {
            const claimed = claimedBy.get(index);
            return (
              <Box key={`${header}-${index}`} sx={{ minWidth: 210, flex: '0 0 auto' }}>
                <Typography
                  sx={{
                    color: 'var(--tsb-fg-muted)',
                    fontSize: 11,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    mb: 0.5,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                  title={header}
                >
                  {header || `Column ${index + 1}`}
                </Typography>

                <TextField
                  select
                  size="small"
                  fullWidth
                  value={claimed ?? UNMAPPED}
                  disabled={busy}
                  onChange={(event) => {
                    const next = event.target.value as GuestField | typeof UNMAPPED;
                    // Clearing the previous owner keeps the one-column-one-field
                    // rule true without the caller having to reason about it.
                    if (claimed) onChange(claimed, undefined);
                    if (next !== UNMAPPED) onChange(next, index);
                  }}
                  SelectProps={{
                    SelectDisplayProps: {
                      'aria-label': `What is the column "${header || index + 1}"?`,
                    },
                  }}
                >
                  <MenuItem value={UNMAPPED}>
                    <Box component="span" sx={{ color: 'var(--tsb-fg-muted)' }}>
                      Don’t import
                    </Box>
                  </MenuItem>
                  {fields.map((spec) => (
                    <MenuItem
                      key={spec.field}
                      value={spec.field}
                      // Taken by another column, unless this is the one holding it.
                      disabled={
                        mapping[spec.field] !== undefined && mapping[spec.field] !== index
                      }
                    >
                      {spec.label}
                    </MenuItem>
                  ))}
                </TextField>

                <Box
                  sx={{
                    mt: 0.75,
                    p: 1,
                    borderRadius: 'var(--tsb-radius-sm)',
                    bgcolor: 'var(--tsb-bg-elevated)',
                    minHeight: 60,
                  }}
                >
                  {sampleRows.map((row, rowIndex) => (
                    <Typography
                      key={rowIndex}
                      sx={{
                        color: 'var(--tsb-fg-body)',
                        fontSize: 11.5,
                        lineHeight: 1.6,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {(row[index] ?? '').trim() || '—'}
                    </Typography>
                  ))}
                </Box>
              </Box>
            );
          })}
        </Stack>
      </Box>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 2 }}>
        <Button
          variant="contained"
          disabled={busy || errors.length > 0}
          onClick={onConfirm}
          sx={GUEST_PRIMARY_BUTTON_SX}
        >
          {busy ? 'Reading…' : `Continue with ${rowCount} ${rowCount === 1 ? 'row' : 'rows'}`}
        </Button>
        <Button variant="outlined" disabled={busy} onClick={onCancel} sx={GUEST_SECONDARY_BUTTON_SX}>
          Cancel
        </Button>
      </Stack>
    </Paper>
  );
}
